import { useId, useState } from 'react';

import styled from 'styled-components';
import React from 'react';
import { dateTimeArray } from '../../../../../../consts/conditions';
import { Flex, Input, Radio as RadioMantine, rem, Select, Text } from '@mantine/core';
import { IoIosArrowDown } from 'react-icons/io';
import { MentionPopover } from '../../../../../../components/MenitionPopover';

export const Radio = ({ type, label, onChangeType, checked, required, ...restProps }) => {
  return (
    <RadioMantine
      {...restProps}
      label={label}
      required={required}
      checked={checked}
      onClick={() => onChangeType(type)}
      size="xs"
    />
  );
};

const ConditionalInput = ({
  onChangeType,
  value,
  onChangeValue,
  columns,
  type,
  hasRequiredError,
  hideVariants,
  formProps,
  containerProps,
  placeholder = 'Choose',
  hiddenVariants = ['datetime', 'ai_input'],
  required,
  disabled = false,
  inputType,
  showDate,
  label,
  onChangeSelectedColumns,
  selectedSuggestions,
  browserUse,
  onBrowserUseChange
}) => {
  const [isOpenOperators, setIsOpenOperators] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // Unique per instance so native radio-button `name` grouping doesn't collide
  // with other ConditionalInput instances (When/Then/Else) on the same page.
  const radioGroupName = useId();

  const onSelectColumn = col => {
    onChangeValue(col);
    setIsOpenOperators(false);
  };

  const isDate = type === 'datetime';
  const isAI = type === 'ai_input';

  const lastValue =
    value && columns?.find(el => el === value)
      ? columns?.find(el => el === value)
      : isAI
        ? value?.input
        : placeholder;

  const renderInput = () => {
    if (['input', 'null'].includes(type))
      return (
        <Input
          id={`grid-table-conditional-input-value-${type || 'empty'}`}
          size="xs"
          value={value}
          disabled={type === 'null' || disabled}
          placeholder="Input"
          type={inputType}
          onChange={e => onChangeValue(e.target.value)}
          w="100%"
          h={rem(30)}
          mah={rem(30)}
          error={hasRequiredError}
        />
      );

    if (isDate && showDate)
      return (
        <Select
          id={`grid-table-conditional-input-date-${type || 'empty'}`}
          placeholder={placeholder}
          data={dateTimeArray}
          value={value}
          searchable
          onChange={onSelectColumn}
          h={rem(30)}
          mah={rem(30)}
          w="100%"
          size="xs"
          rightSection={<IoIosArrowDown />}
          error={hasRequiredError}
        />
      );

    if (type === 'ai_input')
      return (
        <MentionPopover
          id={`grid-table-conditional-input-ai-${type || 'empty'}`}
          text={lastValue}
          isInitialyOpen={isOpen}
          onClosePopover={() => setIsOpen(false)}
          onChange={onChangeValue}
          mentionItems={columns}
          onAdd={onChangeSelectedColumns}
          selectedSuggestions={selectedSuggestions}
          browserUse={browserUse}
          onBrowserUseChange={onBrowserUseChange}
        />
      );

    return (
      <Select
        id={`grid-table-conditional-input-select-${type || 'empty'}`}
        placeholder={placeholder}
        data={columns}
        value={lastValue}
        searchable
        onChange={onSelectColumn}
        h={rem(30)}
        mah={rem(30)}
        w="100%"
        size="xs"
        error={hasRequiredError}
        rightSection={<IoIosArrowDown />}
      />
    );
  };

  return (
    <InputContainer
      id={`grid-table-conditional-input-container-${type || 'empty'}`}
      key={type}
      isOpenItems={isOpenOperators}
      {...containerProps}
    >
      {!hideVariants && (
        <div id={`grid-table-conditional-input-variants-shell-${type || 'empty'}`}>
          <Flex
            id={`grid-table-conditional-input-variants-row-${type || 'empty'}`}
            justify="space-between"
            align="center"
          >
            {label && (
              <Text
                id={`grid-table-conditional-input-label-${type || 'empty'}`}
                size={rem(12)}
                fw={600}
                c="#25262B"
                dangerouslySetInnerHTML={{ __html: label }}
              />
            )}

            <Flex
              id={`grid-table-conditional-input-variant-buttons-${type || 'empty'}`}
              {...formProps}
              gap={rem(8)}
            >
              {!hiddenVariants.includes('input') && (
                <Radio
                  id={`grid-table-conditional-input-input-${type || 'empty'}`}
                  hideCheckbox={hiddenVariants.length === 2}
                  type="input"
                  name={radioGroupName}
                  checked={type === 'input'}
                  onChangeType={onChangeType}
                  label="Input"
                  required={required}
                  hasRequiredError={hasRequiredError}
                />
              )}

              {!hiddenVariants.includes('column') && (
                <Radio
                  id={`grid-table-conditional-input-column-${type || 'empty'}`}
                  hideCheckbox={hiddenVariants.length === 2}
                  type="column"
                  name={radioGroupName}
                  checked={type === 'column'}
                  onChangeType={onChangeType}
                  label="Column"
                  required={required}
                  hasRequiredError={hasRequiredError}
                />
              )}

              {!hiddenVariants.includes('null') && (
                <Radio
                  id={`grid-table-conditional-input-null-${type || 'empty'}`}
                  hideCheckbox={hiddenVariants.length === 2}
                  type="null"
                  name={radioGroupName}
                  label="Null"
                  checked={type === 'null'}
                  required={required}
                  onChangeType={onChangeType}
                  hasRequiredError={hasRequiredError}
                />
              )}

              {showDate && (
                <Radio
                  id={`grid-table-conditional-input-date-toggle-${type || 'empty'}`}
                  hideCheckbox={hiddenVariants.length === 2}
                  type="datetime"
                  name={radioGroupName}
                  label="Date"
                  checked={type === 'datetime'}
                  required={required}
                  onChangeType={onChangeType}
                  hasRequiredError={hasRequiredError}
                />
              )}

              {!hiddenVariants.includes('ai_input') && (
                <Radio
                  id={`grid-table-conditional-input-ai-toggle-${type || 'empty'}`}
                  hideCheckbox={hiddenVariants.length === 2}
                  type="ai_input"
                  name={radioGroupName}
                  label="AI"
                  checked={type === 'ai_input'}
                  required={required}
                  onChangeType={val => {
                    onChangeType(val);
                    setIsOpen(true);
                  }}
                  hasRequiredError={hasRequiredError}
                />
              )}
            </Flex>
          </Flex>
        </div>
      )}

      {renderInput()}
    </InputContainer>
  );
};

export default ConditionalInput;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  .input {
    max-height: 30px !important;
    min-height: 30px !important;
  }

  label {
    font-size: 12px;
    padding: 0 4px;
  }
`;
